import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatDate } from "../lib/api";
import { MODULE_SCHEMAS } from "../lib/moduleSchemas";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { AlertTriangle, CheckCircle2, Pencil, X, Save } from "lucide-react";
import { toast } from "sonner";

export default function Nominees() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // entity_id currently inline-editing
  const [editForm, setEditForm] = useState({ nominee: "", nominee_relationship: "", nominee_verified: false });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/nominees");
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (r) => {
    setEditing(r.entity_id);
    setEditForm({
      nominee: r.nominee || "",
      nominee_relationship: r.nominee_relationship || "",
      nominee_verified: !!r.nominee_verified,
    });
  };
  const cancelEdit = () => { setEditing(null); };

  const saveEdit = async (r) => {
    setSaving(true);
    try {
      // Fetch the entity with mask=false, merge nominee fields, PUT
      const { data: current } = await api.get(`/entities/${r.module}/${r.entity_id}?mask=false`);
      await api.put(`/entities/${r.module}/${r.entity_id}`, {
        data: {
          ...current.data,
          nominee: editForm.nominee,
          nominee_relationship: editForm.nominee_relationship,
          nominee_verified: editForm.nominee_verified,
        },
      });
      toast.success("Nominee updated");
      setEditing(null);
      await load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const missing = rows.filter((r) => !r.nominee);
  const verified = rows.filter((r) => r.nominee && r.nominee_verified);

  return (
    <div className="space-y-6" data-testid="nominees-page">
      <div>
        <div className="label-overline">Nominee Tracker</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-1">
          Nominee Coverage
        </h1>
        <p className="text-slate-600 mt-2 text-sm max-w-2xl">
          Track nominee status across every account, policy and asset.
          Click <span className="inline-flex items-center gap-1 text-slate-900 font-medium"><Pencil className="h-3 w-3" /> Fix</span> to set or verify a nominee inline — no form jumps.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-5">
          <div className="label-overline">Total Tracked</div>
          <div className="stat-value mt-2">{rows.length}</div>
        </Card>
        <Card className="p-5 bg-emerald-50 border-emerald-200">
          <div className="label-overline text-emerald-700">Verified</div>
          <div className="stat-value mt-2 text-emerald-900">{verified.length}</div>
        </Card>
        <Card className="p-5 bg-amber-50 border-amber-200">
          <div className="label-overline text-amber-700">Missing Nominee</div>
          <div className="stat-value mt-2 text-amber-900">{missing.length}</div>
        </Card>
      </div>

      {loading ? (
        <div className="py-12 text-center text-slate-500 text-sm">Loading...</div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left">
                  <th className="px-4 py-3 font-medium text-slate-700">Account / Asset</th>
                  <th className="px-4 py-3 font-medium text-slate-700">Module</th>
                  <th className="px-4 py-3 font-medium text-slate-700">Institution</th>
                  <th className="px-4 py-3 font-medium text-slate-700 min-w-[260px]">Nominee</th>
                  <th className="px-4 py-3 font-medium text-slate-700">Status</th>
                  <th className="px-4 py-3 font-medium text-slate-700 w-24 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isEditing = editing === r.entity_id;
                  return (
                  <tr key={r.entity_id} className="border-b border-slate-100 hover:bg-slate-50/50 align-top">
                    <td className="px-4 py-3">
                      <Link to={`/m/${r.module}`} className="font-medium text-slate-900 hover:underline">
                        {r.title}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="text-[10px]">
                        {MODULE_SCHEMAS[r.module]?.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{r.institution || "—"}</td>
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <div className="space-y-2">
                          <Input
                            placeholder="Nominee name"
                            value={editForm.nominee}
                            onChange={(e) => setEditForm({ ...editForm, nominee: e.target.value })}
                            data-testid={`nominee-input-name-${r.entity_id}`}
                          />
                          <Input
                            placeholder="Relationship (e.g., Spouse)"
                            value={editForm.nominee_relationship}
                            onChange={(e) => setEditForm({ ...editForm, nominee_relationship: e.target.value })}
                            data-testid={`nominee-input-rel-${r.entity_id}`}
                          />
                          <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                            <Checkbox
                              checked={editForm.nominee_verified}
                              onCheckedChange={(v) => setEditForm({ ...editForm, nominee_verified: !!v })}
                              data-testid={`nominee-input-verified-${r.entity_id}`}
                            />
                            Nominee is verified with the institution
                          </label>
                        </div>
                      ) : r.nominee ? (
                        <div>
                          <div className="font-medium">{r.nominee}</div>
                          {r.nominee_relationship && (
                            <div className="text-xs text-slate-500">{r.nominee_relationship}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400 italic">Not set</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {!r.nominee ? (
                        <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" />Missing</Badge>
                      ) : r.nominee_verified ? (
                        <Badge className="bg-emerald-600 hover:bg-emerald-600 gap-1"><CheckCircle2 className="h-3 w-3" />Verified</Badge>
                      ) : (
                        <Badge variant="secondary">Set · verify</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isEditing ? (
                        <div className="inline-flex gap-1">
                          <Button size="icon" variant="ghost" onClick={cancelEdit}
                            data-testid={`nominee-cancel-${r.entity_id}`}>
                            <X className="h-4 w-4" />
                          </Button>
                          <Button size="icon" onClick={() => saveEdit(r)} disabled={saving}
                            data-testid={`nominee-save-${r.entity_id}`}>
                            <Save className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button size="sm" variant={r.nominee ? "ghost" : "outline"}
                          onClick={() => startEdit(r)}
                          data-testid={`nominee-fix-${r.entity_id}`}>
                          <Pencil className="h-3.5 w-3.5 mr-1" /> {r.nominee ? "Edit" : "Fix"}
                        </Button>
                      )}
                    </td>
                  </tr>
                );})}
                {rows.length === 0 && (
                  <tr><td colSpan={6} className="p-10 text-center text-slate-500">
                    No tracked accounts yet. Start adding banks, investments or insurance.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
